// src/features/simulators/mobile-desk/hooks/useMobileDeskSimulation.ts

import { useCallback } from 'react';
import type { AnalyticsClient } from '../../shared/utils/analyticsClient';
import type { LogEntry } from '../../shared/types/simulator.types';
import type { SimulationSummary } from '../../shared/hooks/useSimulatorWizard';
import type { MobileDeskConfigValues } from '../components/MobileDeskConfigForm';
import {
  JourneyEventCode,
  SectionCode,
  GuestType,
  KeyDeliveryMethod,
  createCorrelatedEvent,
} from '../../shared/types/journey.types';
import {
  generateAlternateReservationNumbers,
  generateRoomNumber,
  generatePaymentData,
  generateSectionDuration,
  createJourneyState,
  generateGuestName,
} from '../../shared/utils/journeyDataGenerator';
import {
  DocumentScanMethod,
  MOBILE_DESK_CHANNEL,
} from '../types';

type LogFn = (type: LogEntry['type'], message: string, details?: any) => void;

const ERROR_RATE = 0.05;

export const useMobileDeskSimulation = () => {
  const simulate = useCallback(async (
    client: AnalyticsClient,
    addLog: LogFn,
    deviceId: string,
    hotelId: string,
    config: MobileDeskConfigValues,
  ): Promise<SimulationSummary> => {
    const startTime = Date.now();
    let totalEvents = 0;
    let successful = 0;
    let failed = 0;

    // Determine reservation number to use
    const reservationToUse = (!config.autoGenerateReservation && config.reservationNumber)
      ? config.reservationNumber
      : undefined;

    // Create journey state
    const journey = createJourneyState({
      guestType: config.guestType,
      hasPreCheckIn: false,
      reservationNumber: reservationToUse,
    });

    // Add alternate reservation numbers randomly
    if (Math.random() > 0.7) {
      journey.alternateReservationNumbers = generateAlternateReservationNumbers(journey.reservationNumber!);
      addLog('info', `Multiple reservations: ${journey.reservationNumber} + ${journey.alternateReservationNumbers.join(', ')}`);
    }

    // Assign room number
    journey.roomNumber = generateRoomNumber();

    const isWalking = config.guestType === GuestType.WALKING;

    addLog('info', `Starting ${isWalking ? 'WALKING' : 'RESERVATION'} check-in | Reservation: ${journey.reservationNumber} | Room: ${journey.roomNumber}`);

    // --- CI_START ---
    const ciStartEvent = createCorrelatedEvent(
      JourneyEventCode.CI_START,
      deviceId,
      hotelId,
      journey,
      {
        guestType: config.guestType,
      }
    );
    ciStartEvent.channel = MOBILE_DESK_CHANNEL;
    await client.sendEvent(ciStartEvent);
    totalEvents++;

    await new Promise(resolve => setTimeout(resolve, 500));

    // --- Section S - SEARCH ---
    const searchDuration = generateSectionDuration(SectionCode.S);
    addLog('info', `Section S - Search Reservation (${searchDuration}s)`);

    let enterEvent = createCorrelatedEvent(
      JourneyEventCode.SECTION_ENTER,
      deviceId,
      hotelId,
      journey,
      {
        sectionCode: SectionCode.S,
        action: 'searchReservation',
      }
    );
    enterEvent.channel = MOBILE_DESK_CHANNEL;
    await client.sendEvent(enterEvent);
    journey.currentSection = SectionCode.S;
    await new Promise(resolve => setTimeout(resolve, searchDuration * 100));

    let exitEvent = createCorrelatedEvent(
      JourneyEventCode.SECTION_EXIT,
      deviceId,
      hotelId,
      journey,
      {
        sectionCode: SectionCode.S,
        duration: searchDuration.toString(),
        searchMethod: isWalking ? 'manual' : 'reservation_number',
      }
    );
    exitEvent.channel = MOBILE_DESK_CHANNEL;
    await client.sendEvent(exitEvent);
    journey.completedSections.push(SectionCode.S);
    journey.currentSection = undefined;
    totalEvents += 2;

    await new Promise(resolve => setTimeout(resolve, 300));

    // --- Section I - IDENTIFICATION (with document scanning) ---
    const identifyDuration = generateSectionDuration(SectionCode.I);
    const documentScanError = Math.random() < ERROR_RATE;

    addLog('info', `Section I - Guest Identification (${identifyDuration}s)`);

    enterEvent = createCorrelatedEvent(
      JourneyEventCode.SECTION_ENTER,
      deviceId,
      hotelId,
      journey,
      {
        sectionCode: SectionCode.I,
        action: 'guestIdentification',
      }
    );
    enterEvent.channel = MOBILE_DESK_CHANNEL;
    await client.sendEvent(enterEvent);
    journey.currentSection = SectionCode.I;
    await new Promise(resolve => setTimeout(resolve, identifyDuration * 100));

    // Document scanning logic
    let actualScanMethod = config.documentScanMethod;
    if (documentScanError && config.documentScanMethod === DocumentScanMethod.AUTOMATIC) {
      addLog('warning', 'Document scanner error, switching to manual entry');
      actualScanMethod = DocumentScanMethod.MANUAL;
    }

    // Log scan method used
    if (actualScanMethod === DocumentScanMethod.AUTOMATIC) {
      addLog('info', 'Document scanned via OCR');
    } else if (actualScanMethod === DocumentScanMethod.MANUAL) {
      addLog('info', 'Document entered manually');
    } else if (actualScanMethod === DocumentScanMethod.PHOTO) {
      addLog('info', 'Document captured via photo');
    }

    exitEvent = createCorrelatedEvent(
      JourneyEventCode.SECTION_EXIT,
      deviceId,
      hotelId,
      journey,
      {
        sectionCode: SectionCode.I,
        duration: identifyDuration.toString(),
        idType: 'passport',
        scanMethod: actualScanMethod,
      }
    );
    exitEvent.channel = MOBILE_DESK_CHANNEL;
    await client.sendEvent(exitEvent);
    journey.completedSections.push(SectionCode.I);
    journey.currentSection = undefined;
    totalEvents += 2;

    await new Promise(resolve => setTimeout(resolve, 300));

    // --- Section F - SIGNATURE ---
    const signDuration = generateSectionDuration(SectionCode.F);
    addLog('info', `Section F - Signature Capture (${signDuration}s)`);

    enterEvent = createCorrelatedEvent(
      JourneyEventCode.SECTION_ENTER,
      deviceId,
      hotelId,
      journey,
      {
        sectionCode: SectionCode.F,
        action: 'signatureCapture',
      }
    );
    enterEvent.channel = MOBILE_DESK_CHANNEL;
    await client.sendEvent(enterEvent);
    journey.currentSection = SectionCode.F;
    await new Promise(resolve => setTimeout(resolve, signDuration * 100));

    exitEvent = createCorrelatedEvent(
      JourneyEventCode.SECTION_EXIT,
      deviceId,
      hotelId,
      journey,
      {
        sectionCode: SectionCode.F,
        duration: signDuration.toString(),
        signatureType: 'digital',
      }
    );
    exitEvent.channel = MOBILE_DESK_CHANNEL;
    await client.sendEvent(exitEvent);
    journey.completedSections.push(SectionCode.F);
    journey.currentSection = undefined;
    totalEvents += 2;

    await new Promise(resolve => setTimeout(resolve, 300));

    // --- Section U - UPSELLING ---
    const upsellDuration = generateSectionDuration(SectionCode.U);
    const acceptedUpsell = Math.random() > 0.5;
    addLog('info', `Section U - Upselling (${upsellDuration}s) - ${acceptedUpsell ? 'Accepted' : 'Declined'}`);

    enterEvent = createCorrelatedEvent(
      JourneyEventCode.SECTION_ENTER,
      deviceId,
      hotelId,
      journey,
      {
        sectionCode: SectionCode.U,
        action: 'upsellOffers',
      }
    );
    enterEvent.channel = MOBILE_DESK_CHANNEL;
    await client.sendEvent(enterEvent);
    journey.currentSection = SectionCode.U;
    await new Promise(resolve => setTimeout(resolve, upsellDuration * 100));

    exitEvent = createCorrelatedEvent(
      JourneyEventCode.SECTION_EXIT,
      deviceId,
      hotelId,
      journey,
      {
        sectionCode: SectionCode.U,
        duration: upsellDuration.toString(),
        offersShown: '2',
        offersAccepted: acceptedUpsell ? '1' : '0',
      }
    );
    exitEvent.channel = MOBILE_DESK_CHANNEL;
    await client.sendEvent(exitEvent);
    journey.completedSections.push(SectionCode.U);
    journey.currentSection = undefined;
    totalEvents += 2;

    await new Promise(resolve => setTimeout(resolve, 300));

    // --- Section P - PAYMENT ---
    const paymentDuration = generateSectionDuration(SectionCode.P);
    const paymentFailed = Math.random() < ERROR_RATE;
    const payment = generatePaymentData();

    addLog('info', `Section P - Payment (${paymentDuration}s)`);

    enterEvent = createCorrelatedEvent(
      JourneyEventCode.SECTION_ENTER,
      deviceId,
      hotelId,
      journey,
      {
        sectionCode: SectionCode.P,
        action: 'processPayment',
      }
    );
    enterEvent.channel = MOBILE_DESK_CHANNEL;
    await client.sendEvent(enterEvent);
    journey.currentSection = SectionCode.P;
    await new Promise(resolve => setTimeout(resolve, paymentDuration * 100));

    if (paymentFailed) {
      addLog('error', `Payment FAILED (${payment.amount})`);

      const pmErrorEvent = createCorrelatedEvent(
        JourneyEventCode.PM_ERROR,
        deviceId,
        hotelId,
        journey,
        {
          sectionCode: SectionCode.P,
          errorCode: 'CARD_DECLINED',
          amount: payment.amount.toString(),
          paymentMethod: payment.method,
        }
      );
      pmErrorEvent.channel = MOBILE_DESK_CHANNEL;
      await client.sendEvent(pmErrorEvent);
      totalEvents += 2; // enter + error
      failed++;

      return {
        success: false,
        totalEvents,
        successful,
        failed,
        abandoned: 0,
        duration: Date.now() - startTime,
        error: 'Payment failed',
      };
    }

    // Payment successful
    addLog('success', `Payment SUCCESSFUL (${payment.amount})`);

    const pmSuccessEvent = createCorrelatedEvent(
      JourneyEventCode.PM_SUCCESS,
      deviceId,
      hotelId,
      journey,
      {
        sectionCode: SectionCode.P,
        amount: payment.amount.toString(),
        paymentMethod: payment.method,
        operationId: payment.operationId,
      }
    );
    pmSuccessEvent.channel = MOBILE_DESK_CHANNEL;
    await client.sendEvent(pmSuccessEvent);

    exitEvent = createCorrelatedEvent(
      JourneyEventCode.SECTION_EXIT,
      deviceId,
      hotelId,
      journey,
      {
        sectionCode: SectionCode.P,
        duration: paymentDuration.toString(),
      }
    );
    exitEvent.channel = MOBILE_DESK_CHANNEL;
    await client.sendEvent(exitEvent);
    journey.completedSections.push(SectionCode.P);
    journey.currentSection = undefined;
    totalEvents += 3; // enter + success + exit

    await new Promise(resolve => setTimeout(resolve, 300));

    // --- CI_COMP ---
    addLog('success', `CHECK-IN COMPLETED - Room ${journey.roomNumber}`);

    const ciCompEvent = createCorrelatedEvent(
      JourneyEventCode.CI_COMP,
      deviceId,
      hotelId,
      journey,
      {
        pmsRegistered: 'true',
        totalDuration: Math.floor((Date.now() - startTime) / 1000).toString(),
        keyDeliveryMethod: config.keyDeliveryMethod,
      }
    );
    ciCompEvent.roomNumber = journey.roomNumber;
    ciCompEvent.channel = MOBILE_DESK_CHANNEL;
    // Full name, top-level like a real kiosk: Insight reduces it to «Juan G.».
    const guest = generateGuestName();
    ciCompEvent.guestName = guest.full;
    await client.sendEvent(ciCompEvent);
    journey.checkInCompleted = true;
    totalEvents++;

    await client.updateJourney({
      reservationNumber: journey.reservationNumber!,
      alternateReservationNumbers: journey.alternateReservationNumbers,
      hotelId,
      channel: MOBILE_DESK_CHANNEL,
      eventType: 'CheckInCompleted',
      timestamp: new Date().toISOString(),
    });

    // --- Key Delivery ---
    await new Promise(resolve => setTimeout(resolve, 500));

    const keyDeliveryFailed = Math.random() < 0.02;

    if (keyDeliveryFailed) {
      addLog('error', 'Key delivery failed');

      const kdErrorEvent = createCorrelatedEvent(
        JourneyEventCode.KD_ERROR,
        deviceId,
        hotelId,
        journey,
        {
          sectionCode: SectionCode.K,
          errorCode: 'KEY_DELIVERY_FAILED',
          deliveryMethod: config.keyDeliveryMethod,
        }
      );
      kdErrorEvent.roomNumber = journey.roomNumber;
      kdErrorEvent.channel = MOBILE_DESK_CHANNEL;
      await client.sendEvent(kdErrorEvent);
      totalEvents++;
      failed++;

      return {
        success: false,
        totalEvents,
        successful,
        failed,
        abandoned: 0,
        duration: Date.now() - startTime,
        error: 'Key delivery failed',
      };
    }

    // Key delivery success - log based on method
    const method = config.keyDeliveryMethod;
    if (method === KeyDeliveryMethod.DIGITAL_PIN || method === KeyDeliveryMethod.DIGITAL_MOBILE) {
      addLog('success', 'Digital key sent to guest');
    } else if (method === KeyDeliveryMethod.QR_DISPENSER) {
      addLog('success', 'QR code generated for dispenser pickup');
    } else if (method === KeyDeliveryMethod.MAGNETIC_CARD || method === KeyDeliveryMethod.RFID_CARD || method === KeyDeliveryMethod.CONVENTIONAL_KEY) {
      addLog('success', 'Physical key dispensed');
    } else {
      addLog('success', 'Key handed manually to guest');
    }

    const kdSuccessEvent = createCorrelatedEvent(
      JourneyEventCode.KD_SUCCESS,
      deviceId,
      hotelId,
      journey,
      {
        sectionCode: SectionCode.K,
        deliveryMethod: config.keyDeliveryMethod,
      }
    );
    kdSuccessEvent.roomNumber = journey.roomNumber;
    kdSuccessEvent.channel = MOBILE_DESK_CHANNEL;
    await client.sendEvent(kdSuccessEvent);
    totalEvents++;

    await client.updateJourney({
      reservationNumber: journey.reservationNumber!,
      alternateReservationNumbers: journey.alternateReservationNumbers,
      hotelId,
      channel: MOBILE_DESK_CHANNEL,
      eventType: 'KeyDispensed',
      timestamp: new Date().toISOString(),
    });

    // --- sendCheckIn ---
    await client.sendCheckIn({
      reservationNumber: journey.reservationNumber!,
      alternateReservationNumbers: journey.alternateReservationNumbers,
      guestNameMasked: guest.full,
      documentMasked: `DOC_${journey.reservationNumber!.slice(-4)}`,
      roomNumber: journey.roomNumber!,
      amountCharged: payment.amount,
      paymentMethod: payment.method,
      paymentOperationId: payment.operationId,
      deviceId,
      hotelId,
      channel: MOBILE_DESK_CHANNEL,
      type: isWalking ? 'Walking' : 'Reservation',
    });

    successful++;
    const duration = Date.now() - startTime;
    addLog('success', `Check-in COMPLETE (${(duration / 1000).toFixed(1)}s) | Room: ${journey.roomNumber} | Key: ${config.keyDeliveryMethod}`);

    return {
      success: true,
      totalEvents,
      successful,
      failed,
      abandoned: 0,
      duration,
    };
  }, []);

  return { simulate };
};
